<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useBranding } from 'shared/composables/useBranding';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import BillingAPI from 'dashboard/api/billing';
import OrganizationBillingInvoices from './OrganizationBillingInvoices.vue';
import BillingSeatModifier from 'dashboard/components-next/billing/BillingSeatModifier.vue';
import BillingAddSeatsModal from 'dashboard/components-next/billing/BillingAddSeatsModal.vue';
import PreferredCurrencySelect from './components/PreferredCurrencySelect.vue';
import { useConfig } from 'dashboard/composables/useConfig';
import { getContactPageUrl } from 'dashboard/helper/inboxTroubleshootSteps';
import { useBillingCheckout } from 'dashboard/composables/useBillingCheckout';
import { useBillingAccess } from 'dashboard/composables/useBillingAccess';
import { useKashierReturn } from 'dashboard/composables/useKashierReturn';

const { t } = useI18n();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const { replaceInstallationName } = useBranding();
const store = useStore();

const getAgents = useMapGetter('agents/getAgents');
const getInboxes = useMapGetter('inboxes/getInboxes');
const getAccount = useMapGetter('accounts/getAccount');
const { accountId } = useAccount();

const preferredCurrencyId = computed(
  () => getAccount.value(accountId.value)?.preferred_currency_id
);

const { hostURL } = useConfig();
const contactPageUrl = computed(() => getContactPageUrl(hostURL));

const { isPaying, payInvoice } = useBillingCheckout();
const { loadBillingAccess } = useBillingAccess();
const { handleKashierReturnIfNeeded } = useKashierReturn();

const invoicesRef = ref(null);

const planName = ref('');
const displayStatus = ref('');
const subscriptionEndsAt = ref(null);
const pendingAgents = ref(null);
const pendingInboxes = ref(null);
const pendingInvoiceId = ref(null);
const priceAmount = ref('0.00');
const currencyCode = ref('USD');
const currencySymbol = ref('$');
const inboxesLimit = ref(0);
const agentsLimit = ref(0);
const planBaseAgents = ref(0);
const planBaseInboxes = ref(0);
const minimumAgentsLimit = ref(null);
const minimumInboxesLimit = ref(null);
const agentsUsedFromApi = ref(null);
const inboxesUsedFromApi = ref(null);
const agentsUsed = computed(
  () => agentsUsedFromApi.value ?? getAgents.value?.length ?? 0
);
const inboxesUsed = computed(
  () => inboxesUsedFromApi.value ?? getInboxes.value?.length ?? 0
);

const inboxesPct = computed(() => {
  if (!inboxesLimit.value) return 0;
  return Math.min(100, (inboxesUsed.value / inboxesLimit.value) * 100);
});

const agentsPct = computed(() => {
  if (!agentsLimit.value) return 0;
  return Math.min(100, (agentsUsed.value / agentsLimit.value) * 100);
});

const agentsUsageLabel = computed(() =>
  t('GENERAL_SETTINGS.USAGE_SLASH', {
    used: String(agentsUsed.value),
    max: String(agentsLimit.value),
  })
);

const usageBarFillClass = pct => {
  if (pct >= 95) return 'text-n-ruby-9';
  if (pct >= 80) return 'text-n-amber-9';
  return 'text-n-blue-9';
};

const inboxesUsageLabel = computed(() =>
  t('GENERAL_SETTINGS.USAGE_SLASH', {
    used: String(inboxesUsed.value),
    max: String(inboxesLimit.value),
  })
);

const isFrozen = computed(
  () => (displayStatus.value || '').toLowerCase() === 'frozen'
);

const hasActiveSubscription = computed(() => {
  const s = (displayStatus.value || '').toLowerCase();
  return (
    ['active', 'trial', 'downgrading', 'canceling', 'pending_payment'].includes(
      s
    ) && Boolean(subscriptionEndsAt.value)
  );
});

const isTrial = computed(
  () => (displayStatus.value || '').toLowerCase() === 'trial'
);

const canModifySeats = computed(
  () => hasActiveSubscription.value && !isTrial.value
);

const STATUS_I18N_KEYS = {
  trial: 'BADGE_TRIAL',
  trial_ended: 'BADGE_TRIAL_ENDED',
  active: 'BADGE_ACTIVE',
  pending_payment: 'BADGE_PENDING_PAYMENT',
  downgrading: 'BADGE_DOWNGRADING',
  canceling: 'BADGE_CANCELING',
  frozen: 'BADGE_FROZEN',
  cancelled: 'BADGE_CANCELLED',
  canceled: 'BADGE_CANCELLED',
  expired: 'BADGE_EXPIRED',
};

const statusBadgeText = computed(() => {
  const key = STATUS_I18N_KEYS[(displayStatus.value || '').toLowerCase()];
  if (key) return t(`GENERAL_SETTINGS.BILLING_USAGE_PAGE.${key}`);
  return displayStatus.value || '';
});

const statusBadgeClass = computed(() => {
  const s = (displayStatus.value || '').toLowerCase();
  if (s === 'frozen' || s === 'trial_ended')
    return 'bg-n-ruby-4 text-n-ruby-11';
  if (s === 'pending_payment') return 'bg-n-amber-4 text-n-amber-11';
  if (s === 'trial') return 'bg-n-blue-4 text-n-blue-11';
  if (s === 'active') return 'bg-n-teal-4 text-n-teal-11';
  return 'bg-n-alpha-2 text-n-slate-12';
});

const dangerNote = computed(() =>
  replaceInstallationName(t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.DANGER_NOTE'))
);

const formatMoney = (amount, decimals = 2) =>
  Number(amount).toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

const goSelectPlan = () => {
  router.push(accountScopedRoute('general_settings_billing_plans'));
};

const applyBillingPayload = data => {
  const sub = data?.subscription;
  const limits = data?.limits;
  const nextBilling = data?.next_billing;
  const billingCurrency = data?.billing_currency;

  if (sub?.plan_name) planName.value = sub.plan_name;
  if (sub?.display_status)
    displayStatus.value = String(sub.display_status).toLowerCase();
  else if (sub?.status) displayStatus.value = String(sub.status).toLowerCase();
  pendingAgents.value = sub?.pending_changes?.agents ?? null;
  pendingInboxes.value = sub?.pending_changes?.inboxes ?? null;
  if (sub?.ends_at) {
    subscriptionEndsAt.value = sub.ends_at;
  }
  const effective = sub?.effective_limits;
  const base = sub?.base_limits;
  if (sub) {
    if (effective?.agents != null) agentsLimit.value = effective.agents;
    else if (base?.agents != null) agentsLimit.value = base.agents;
    if (effective?.inboxes != null) inboxesLimit.value = effective.inboxes;
    else if (base?.inboxes != null) inboxesLimit.value = base.inboxes;
  } else {
    if (limits?.agents != null) agentsLimit.value = limits.agents;
    if (limits?.inboxes != null) inboxesLimit.value = limits.inboxes;
  }
  if (sub?.base_limits?.agents != null) {
    planBaseAgents.value = sub.base_limits.agents;
  }
  if (sub?.base_limits?.inboxes != null) {
    planBaseInboxes.value = sub.base_limits.inboxes;
  }
  agentsUsedFromApi.value = sub?.usage?.agents ?? null;
  inboxesUsedFromApi.value = sub?.usage?.inboxes ?? null;
  minimumAgentsLimit.value = sub?.minimum_limits?.agents ?? null;
  minimumInboxesLimit.value = sub?.minimum_limits?.inboxes ?? null;
  pendingInvoiceId.value = data?.pending_invoice?.id ?? null;

  if (nextBilling) {
    priceAmount.value = formatMoney(nextBilling.amount);
    currencyCode.value = nextBilling.currency_code || currencyCode.value;
    currencySymbol.value =
      nextBilling.currency_symbol ||
      nextBilling.currency_code ||
      currencySymbol.value;
  } else if (billingCurrency) {
    currencyCode.value = billingCurrency.code;
    currencySymbol.value = billingCurrency.symbol || billingCurrency.code;
  }
};

const loadBilling = async () => {
  try {
    const { data } = await BillingAPI.getSubscription();
    applyBillingPayload(data);
    await loadBillingAccess();
  } catch {
    useAlert(t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.BILLING_LOAD_ERROR'));
  }
};

const onPreferredCurrencyUpdated = () => {
  loadBilling();
};

const onPayPendingInvoice = async () => {
  if (!pendingInvoiceId.value) return;
  await payInvoice(pendingInvoiceId.value);
};

const addSeatsModalRef = ref(null);
const reduceSeatsModalRef = ref(null);

const openAddSeatsModal = () => {
  addSeatsModalRef.value?.open();
};

const openReduceSeatsModal = () => {
  reduceSeatsModalRef.value?.open();
};

onMounted(async () => {
  store.dispatch('agents/get');
  store.dispatch('inboxes/get');
  const kashierResult = await handleKashierReturnIfNeeded();
  await loadBilling();
  if (kashierResult?.handled) {
    await invoicesRef.value?.reload?.();
  }
});

watch(preferredCurrencyId, () => {
  loadBilling();
});
</script>

<template>
  <div class="flex w-full max-w-none min-w-0 flex-col gap-6">
    <BaseSettingsHeader
      :title="t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.TITLE')"
      :description="t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.DESCRIPTION')"
    >
      <template #headerTrailing>
        <PreferredCurrencySelect @updated="onPreferredCurrencyUpdated" />
      </template>
    </BaseSettingsHeader>

    <div
      v-if="isFrozen"
      class="mx-8 flex flex-col gap-3 rounded-xl border border-solid border-n-ruby-8 bg-n-ruby-4 p-4 md:mx-10"
    >
      <p class="mb-0 text-sm font-semibold text-n-ruby-11">
        {{ t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.FROZEN_TITLE') }}
      </p>
      <p class="mb-0 text-sm text-n-ruby-11">
        {{ t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.FROZEN_DESCRIPTION') }}
      </p>
      <Button
        v-if="pendingInvoiceId"
        size="sm"
        class="w-fit"
        :label="t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.PAY_INVOICE')"
        :is-loading="isPaying"
        @click="onPayPendingInvoice"
      />
    </div>

    <div class="flex w-full min-w-0 flex-col gap-6 px-8 md:px-10">
      <div
        class="flex flex-col gap-0 rounded-xl border border-solid border-n-weak bg-n-surface-1 outline outline-1 outline-n-weak -outline-offset-1"
      >
        <div
          class="flex flex-col gap-4 border-0 border-b border-solid border-n-weak p-5 sm:flex-row sm:items-start sm:justify-between"
        >
          <div class="flex min-w-0 flex-1 flex-col gap-3">
            <div
              class="flex flex-wrap items-center gap-2 text-lg font-bold leading-snug text-n-slate-12 sm:text-xl"
            >
              <span class="text-n-slate-11">
                {{ t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.CURRENT_PLAN') }}
              </span>
              <span class="capitalize">
                {{ planName }}
              </span>
              <span
                v-if="statusBadgeText"
                class="rounded-md px-2 py-0.5 text-xs font-bold capitalize"
                :class="statusBadgeClass"
              >
                {{ statusBadgeText }}
              </span>
            </div>
            <div class="flex flex-col gap-2">
              <span class="text-sm font-bold text-n-slate-12">
                {{ t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.USAGE_LABEL') }}
              </span>
              <div class="flex w-full max-w-md flex-col gap-2.5">
                <div class="flex min-w-0 items-center gap-2 sm:gap-3">
                  <span
                    class="w-14 shrink-0 text-xs font-medium text-n-slate-11 sm:w-16"
                  >
                    {{
                      t(
                        'GENERAL_SETTINGS.BILLING_USAGE_PAGE.INBOXES_USAGE_LABEL'
                      )
                    }}
                  </span>
                  <svg
                    class="block h-2.5 min-w-0 flex-1 text-n-slate-4 sm:h-3"
                    viewBox="0 0 100 4"
                    preserveAspectRatio="none"
                    aria-hidden="true"
                  >
                    <rect width="100" height="4" fill="currentColor" rx="1" />
                    <rect
                      :width="inboxesPct"
                      height="4"
                      :class="usageBarFillClass(inboxesPct)"
                      fill="currentColor"
                      rx="1"
                    />
                  </svg>
                  <div class="flex shrink-0 items-center gap-1.5">
                    <span
                      class="text-xs font-medium tabular-nums text-n-slate-12"
                    >
                      {{ inboxesUsageLabel }}
                    </span>
                    <button
                      type="button"
                      class="m-0 cursor-pointer border-0 bg-transparent p-0 text-xs font-medium text-n-blue-11 hover:underline disabled:cursor-not-allowed disabled:text-n-slate-10 disabled:no-underline"
                      :disabled="!canModifySeats"
                      @click="openAddSeatsModal"
                    >
                      {{
                        t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.ADD_MORE_SEATS')
                      }}
                    </button>
                  </div>
                </div>
                <div class="flex min-w-0 items-center gap-2 sm:gap-3">
                  <span
                    class="w-14 shrink-0 text-xs font-medium text-n-slate-11 sm:w-16"
                  >
                    {{
                      t(
                        'GENERAL_SETTINGS.BILLING_USAGE_PAGE.AGENTS_USAGE_LABEL'
                      )
                    }}
                  </span>
                  <svg
                    class="block h-2.5 min-w-0 flex-1 text-n-slate-4 sm:h-3"
                    viewBox="0 0 100 4"
                    preserveAspectRatio="none"
                    aria-hidden="true"
                  >
                    <rect width="100" height="4" fill="currentColor" rx="1" />
                    <rect
                      :width="agentsPct"
                      height="4"
                      :class="usageBarFillClass(agentsPct)"
                      fill="currentColor"
                      rx="1"
                    />
                  </svg>
                  <div class="flex shrink-0 items-center gap-1.5">
                    <span
                      class="text-xs font-medium tabular-nums text-n-slate-12"
                    >
                      {{ agentsUsageLabel }}
                    </span>
                    <button
                      type="button"
                      class="m-0 cursor-pointer border-0 bg-transparent p-0 text-xs font-medium text-n-blue-11 hover:underline disabled:cursor-not-allowed disabled:text-n-slate-10 disabled:no-underline"
                      :disabled="!canModifySeats"
                      @click="openAddSeatsModal"
                    >
                      {{
                        t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.ADD_MORE_SEATS')
                      }}
                    </button>
                  </div>
                </div>
              </div>
              <BillingAddSeatsModal
                ref="addSeatsModalRef"
                :plan-name="planName"
                :disabled="!canModifySeats"
                @updated="loadBilling"
              />
              <BillingSeatModifier
                ref="reduceSeatsModalRef"
                :agents-limit="agentsLimit"
                :inboxes-limit="inboxesLimit"
                :agents-base-limit="planBaseAgents"
                :inboxes-base-limit="planBaseInboxes"
                :agents-used="agentsUsed"
                :inboxes-used="inboxesUsed"
                :agents-minimum-limit="minimumAgentsLimit"
                :inboxes-minimum-limit="minimumInboxesLimit"
                :pending-agents="pendingAgents"
                :pending-inboxes="pendingInboxes"
                :disabled="!canModifySeats"
                @updated="loadBilling"
              />
              <div class="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  class="m-0 cursor-pointer border-0 bg-transparent p-0 text-sm font-medium text-n-blue-11 hover:underline"
                  @click="goSelectPlan"
                >
                  {{
                    t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.SHOW_ALL_FEATURES')
                  }}
                </button>
                <button
                  v-if="canModifySeats"
                  type="button"
                  class="m-0 cursor-pointer border-0 bg-transparent p-0 text-sm font-medium text-n-blue-11 hover:underline"
                  @click="openReduceSeatsModal"
                >
                  {{
                    t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.SEATS_REDUCE_TITLE')
                  }}
                </button>
              </div>
            </div>
          </div>
          <div
            class="flex shrink-0 flex-col items-stretch gap-3 sm:items-end sm:text-end"
          >
            <div class="tabular-nums">
              <span class="text-2xl font-semibold text-n-slate-12">
                {{ currencySymbol }}{{ priceAmount }}
              </span>
              <span class="ms-1 text-sm text-n-slate-11">{{
                currencyCode
              }}</span>
            </div>
            <p class="mb-0 text-sm text-n-slate-11">
              {{ t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.BILLED_NEXT') }}
            </p>
            <Button
              v-if="!hasActiveSubscription"
              :label="t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.SUBSCRIBE_CTA')"
              size="md"
              class="w-full sm:w-auto"
              @click="goSelectPlan"
            />
          </div>
        </div>

        <OrganizationBillingInvoices ref="invoicesRef" />
      </div>

      <div class="flex flex-col gap-3">
        <h3 class="mb-0 text-base font-semibold text-n-slate-12">
          {{ t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.DANGER_TITLE') }}
        </h3>
        <div
          class="flex gap-3 rounded-lg border border-solid border-n-ruby-8 bg-n-ruby-4 p-4"
        >
          <Icon
            icon="i-lucide-circle-alert"
            class="size-5 shrink-0 text-n-ruby-9"
          />
          <p class="mb-0 text-sm text-n-ruby-11">
            {{ dangerNote }}
          </p>
        </div>
        <a
          :href="contactPageUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="m-0 w-fit cursor-pointer border-0 bg-transparent p-0 text-sm font-medium text-n-ruby-11 no-underline hover:underline"
        >
          {{ t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.DANGER_CONTACT') }}
        </a>
      </div>
    </div>
  </div>
</template>
